import { PageHeader } from '../components/ui/PageHeader';
import { Icon } from '../components/ui/Icon';
import { Button } from '../components/ui/primitives';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  createStore,
  getStore,
  getStores,
  getStoreUsers,
  setStoreActive,
  updateStore,
} from '../api/stores';
import { getErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { canManageStores } from '../auth/permissions';
import { Modal } from '../components/Modal';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { roleLabel } from '../components/BusinessUi';
import type { Store, StoreAssignedUser } from '../types/stores';
import { DEFAULT_STORE_TIMEZONE, MEXICO_TIMEZONE_OPTIONS } from '../utils/timezones';
import { userDisplayNameOr } from '../utils/userDisplayName';

interface StoreFormState {
  name: string;
  externalId: string;
  city: string;
  region: string;
  countryCode: string;
  timezone: string;
}

const emptyStoreForm: StoreFormState = {
  name: '',
  externalId: '',
  city: '',
  region: '',
  countryCode: 'MX',
  timezone: 'America/Mexico_City',
};

export function StoresPage() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isOwner = canManageStores(user?.role);
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<string>();
  const [success, setSuccess] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [form, setForm] = useState<StoreFormState>(emptyStoreForm);
  const [formError, setFormError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [busyStoreId, setBusyStoreId] = useState<string>();
  const [detailStore, setDetailStore] = useState<Store | null>();
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string>();
  const [usersStore, setUsersStore] = useState<Store | null>();
  const [assignedUsers, setAssignedUsers] = useState<StoreAssignedUser[]>([]);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string>();

  const loadStores = useCallback(async () => {
    setPageError(undefined);
    setIsLoading(true);

    try {
      const result = await getStores();
      setStores(result.stores);
    } catch (error) {
      setPageError(getErrorMessage(error, 'No fue posible cargar las tiendas.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStores();
  }, [loadStores]);

  const openCreate = () => {
    if (!isOwner) return;
    setEditingStore(null);
    setForm(emptyStoreForm);
    setFormError(undefined);
    setIsCreating(true);
  };

  const openEdit = (target: Store) => {
    if (!isOwner) return;
    setIsCreating(false);
    setEditingStore(target);
    setForm({
      name: target.name,
      externalId: target.externalId,
      city: target.city || '',
      region: target.region || '',
      countryCode: target.countryCode || 'MX',
      timezone: target.timezone,
    });
    setFormError(undefined);
  };

  const closeForm = () => {
    if (isSaving) return;
    setEditingStore(null);
    setIsCreating(false);
    setFormError(undefined);
  };

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isOwner || isSaving) return;

    const validationError = validateStoreForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError(undefined);
    setIsSaving(true);

    try {
      const normalized = {
        name: form.name.trim(),
        timezone: form.timezone.trim(),
        countryCode: form.countryCode.trim().toUpperCase(),
      };

      if (editingStore) {
        const result = await updateStore(editingStore.id, {
          ...normalized,
          externalId: form.externalId.trim(),
          city: form.city.trim() || null,
          region: form.region.trim() || null,
        });
        replaceStore(result.store);
        setSuccess(`Tienda ${result.store.name} actualizada.`);
      } else {
        const result = await createStore({
          ...normalized,
          ...(form.externalId.trim() ? { externalId: form.externalId.trim() } : {}),
          ...(form.city.trim() ? { city: form.city.trim() } : {}),
          ...(form.region.trim() ? { region: form.region.trim() } : {}),
        });
        setStores((current) => [...current, result.store]);
        setSuccess(`Tienda ${result.store.name} creada.`);
      }

      setEditingStore(null);
      setIsCreating(false);
    } catch (error) {
      setFormError(getErrorMessage(error, 'No fue posible guardar la tienda.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleActiveChange = async (target: Store) => {
    if (!isOwner || busyStoreId) return;
    const nextActive = !target.isActive;

    if (!nextActive && !(await confirm({ title: 'Desactivar tienda', description: `¿Desactivar ${target.name}? Las asignaciones existentes se conservarán.`, confirmLabel: 'Desactivar', danger: true }))) {
      return;
    }

    setBusyStoreId(target.id);
    setPageError(undefined);
    setSuccess(undefined);

    try {
      const result = await setStoreActive(target.id, nextActive);
      replaceStore(result.store);
      setSuccess(`Tienda ${nextActive ? 'activada' : 'desactivada'} correctamente.`);
    } catch (error) {
      setPageError(getErrorMessage(error, 'No fue posible cambiar el estado de la tienda.'));
    } finally {
      setBusyStoreId(undefined);
    }
  };

  const openDetail = async (target: Store) => {
    setDetailStore(target);
    setDetailError(undefined);
    setIsDetailLoading(true);

    try {
      const result = await getStore(target.id);
      setDetailStore(result.store);
    } catch (error) {
      setDetailError(getErrorMessage(error, 'No fue posible consultar la tienda.'));
    } finally {
      setIsDetailLoading(false);
    }
  };

  const openAssignedUsers = async (target: Store) => {
    setUsersStore(target);
    setAssignedUsers([]);
    setUsersError(undefined);
    setIsUsersLoading(true);

    try {
      const result = await getStoreUsers(target.id);
      setUsersStore(result.store);
      setAssignedUsers(result.users);
    } catch (error) {
      setUsersError(getErrorMessage(error, 'No fue posible consultar los usuarios asignados.'));
    } finally {
      setIsUsersLoading(false);
    }
  };

  const replaceStore = (updatedStore: Store) => {
    setStores((current) => current.map((item) => item.id === updatedStore.id ? updatedStore : item));
  };

  const showForm = isCreating || editingStore !== null;

  return (
    <div className="admin-page n3-screen">
      {/* P-075: el título es el del menú («Tiendas»), y un solo encabezado con su acción. */}
      <PageHeader
        actions={isOwner ? <Button disabled={isLoading} iconStart={<Icon name="plus" size={18} />} onClick={openCreate} variant="primary">Nueva tienda</Button> : undefined}
        description={isOwner ? 'Administra sucursales y consulta sus usuarios.' : 'Las sucursales asignadas a tu cuenta.'}
        title="Tiendas"
      />

      {pageError && <div className="alert alert--error">{pageError}</div>}
      {success && <div className="alert alert--success">{success}</div>}

      <section className="admin-card">
        <div className="admin-card__summary">
          <span><strong>{stores.length}</strong> tiendas visibles</span>
          <span><strong>{stores.filter((item) => item.isActive).length}</strong> activas</span>
        </div>

        {isLoading ? (
          <div className="inline-loading"><span className="spinner" />Cargando tiendas...</div>
        ) : stores.length === 0 ? (
          <div className="empty-panel"><strong>No hay tiendas disponibles</strong><span>{isOwner ? 'Crea una sucursal para comenzar.' : 'Solicita al administrador una asignación.'}</span></div>
        ) : (
          <div className="store-admin-grid">
            {stores.map((item) => {
              const isBusy = busyStoreId === item.id;
              return (
                <article className={`store-admin-card ${!item.isActive ? 'store-admin-card--inactive' : ''}`} key={item.id}>
                  <div className="store-admin-card__top">
                    <span className="store-admin-card__mark">{item.name.charAt(0).toUpperCase()}</span>
                    <span className={`status-pill ${item.isActive ? '' : 'status-pill--inactive'}`}>{item.isActive ? 'Activa' : 'Inactiva'}</span>
                  </div>
                  <h2>{item.name}</h2>
                  <p>{[item.city, item.region, item.countryCode].filter(Boolean).join(', ') || 'Ubicación no registrada'}</p>
                  <dl className="store-meta"><div><dt>Zona horaria</dt><dd>{item.timezone}</dd></div><div><dt>Origen</dt><dd>{item.isFromLoyverse ? 'Loyverse' : 'Manual'}</dd></div></dl>
                  <div className="store-admin-card__actions">
                    <button className="text-button" onClick={() => void openDetail(item)} type="button">Detalle</button>
                    <button className="text-button" onClick={() => void openAssignedUsers(item)} type="button">Usuarios</button>
                    {isOwner && <button className="text-button" onClick={() => openEdit(item)} type="button">Editar</button>}
                    {isOwner && <button className={`text-button ${item.isActive ? 'text-button--danger' : 'text-button--success'}`} disabled={isBusy} onClick={() => void handleActiveChange(item)} type="button">{isBusy ? 'Procesando...' : item.isActive ? 'Desactivar' : 'Activar'}</button>}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {showForm && (
        <Modal
          description="No se eliminan tiendas físicamente; puedes cambiar su estado después."
          isBusy={isSaving}
          onClose={closeForm}
          title={editingStore ? `Editar ${editingStore.name}` : 'Crear tienda'}
          footer={<><button className="button button--ghost" disabled={isSaving} onClick={closeForm} type="button">Cancelar</button><button className="button button--primary" disabled={isSaving} form="store-form" type="submit">{isSaving ? 'Guardando...' : 'Guardar tienda'}</button></>}
        >
          <form id="store-form" onSubmit={handleFormSubmit}>
            {formError && <div className="alert alert--error">{formError}</div>}
            <div className="form-grid form-grid--compact">
              <label className="field form-grid__full"><span>Nombre</span><input maxLength={200} onChange={(event) => setForm({ ...form, name: event.target.value })} required value={form.name} /></label>
              <label className="field"><span>Ciudad <small>opcional</small></span><input maxLength={150} onChange={(event) => setForm({ ...form, city: event.target.value })} value={form.city} /></label>
              <label className="field"><span>Región <small>opcional</small></span><input maxLength={150} onChange={(event) => setForm({ ...form, region: event.target.value })} value={form.region} /></label>
              <label className="field"><span>País</span><input maxLength={2} onChange={(event) => setForm({ ...form, countryCode: event.target.value.toUpperCase() })} required value={form.countryCode} /></label>
              <label className="field">
                <span>Zona horaria</span>
                <select onChange={(event) => setForm({ ...form, timezone: event.target.value })} required value={form.timezone}>
                  {/* Si la tienda ya tenía una zona fuera de esta lista (ej. capturada antes de este selector), se conserva como opción para no cambiarla sin querer al guardar. */}
                  {!MEXICO_TIMEZONE_OPTIONS.some(([value]) => value === form.timezone) && form.timezone && (
                    <option value={form.timezone}>{form.timezone}</option>
                  )}
                  {MEXICO_TIMEZONE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label className="field form-grid__full"><span>Identificador de integración <small>{editingStore ? 'necesario para conservar la conexión' : 'opcional'}</small></span><input disabled={Boolean(editingStore?.isFromLoyverse)} maxLength={200} onChange={(event) => setForm({ ...form, externalId: event.target.value })} required={Boolean(editingStore)} value={form.externalId} /></label>
            </div>
          </form>
        </Modal>
      )}

      {detailStore && (
        <Modal onClose={() => setDetailStore(null)} title={detailStore.name} description="Detalle de la sucursal">
          {detailError && <div className="alert alert--error">{detailError}</div>}
          {isDetailLoading ? <div className="inline-loading"><span className="spinner" />Consultando detalle...</div> : (
            <dl className="detail-list">
              <div><dt>Identificador de integración</dt><dd>{detailStore.externalId}</dd></div>
              <div><dt>Ciudad</dt><dd>{detailStore.city || 'Sin dato'}</dd></div>
              <div><dt>Región</dt><dd>{detailStore.region || 'Sin dato'}</dd></div>
              <div><dt>País</dt><dd>{detailStore.countryCode || 'Sin dato'}</dd></div>
              <div><dt>Zona horaria</dt><dd>{detailStore.timezone}</dd></div>
              <div><dt>Estado</dt><dd>{detailStore.isActive ? 'Activa' : 'Inactiva'}</dd></div>
              <div><dt>Creada</dt><dd>{formatDate(detailStore.createdAt, detailStore.timezone)}</dd></div>
              <div><dt>Actualizada</dt><dd>{formatDate(detailStore.updatedAt, detailStore.timezone)}</dd></div>
            </dl>
          )}
        </Modal>
      )}

      {usersStore && (
        <Modal onClose={() => setUsersStore(null)} title={`Usuarios de ${usersStore.name}`} description="Asignaciones existentes en esta sucursal">
          {usersError && <div className="alert alert--error">{usersError}</div>}
          {isUsersLoading ? <div className="inline-loading"><span className="spinner" />Consultando usuarios...</div> : assignedUsers.length === 0 ? (
            <div className="empty-panel empty-panel--small"><strong>Sin usuarios asignados</strong><span>Las asignaciones se gestionan desde Usuarios.</span>{/* P-078 (BK31): el camino, a un toque. */}<div className="empty-panel__actions"><a className="text-button" href="#users">Ir a Usuarios →</a></div></div>
          ) : (
            <div className="compact-list">
              {assignedUsers.map((assignedUser) => (
                <article className={!assignedUser.isActive ? 'compact-list__item compact-list__item--inactive' : 'compact-list__item'} key={assignedUser.id}>
                  <span className="compact-list__avatar">{assignedUser.username.slice(0, 2).toUpperCase()}</span>
                  {/* N360-04: a un EMPLOYEE el backend no le manda correo ni rol de sus compañeros. */}
                  <div><strong>{userDisplayNameOr(assignedUser)}</strong>{assignedUser.email && <small>{assignedUser.email}</small>}</div>
                  {assignedUser.role && <span className={`role-pill role-pill--${assignedUser.role.toLowerCase()}`}>{roleLabel(assignedUser.role)}</span>}
                </article>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function validateStoreForm(form: StoreFormState): string | undefined {
  if (!form.name.trim()) return 'El nombre de la tienda es requerido.';
  if (!form.timezone.trim()) return 'La zona horaria es requerida.';
  if (!/^[A-Za-z]{2}$/.test(form.countryCode.trim())) return 'El código de país debe tener dos letras.';
  return undefined;
}

function formatDate(value: string, timeZone: string = DEFAULT_STORE_TIMEZONE): string {
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(value));
}
